import { useEffect, useState } from "react";
import { Plus, ArrowRight, ShieldCheck, X, ChevronRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { bcReviews, bcReviewCreate, bcReviewGet, bcReviewPatch, bcReviewTransition, bcReviewStates, bcTools } from "@/lib/api";

const STATE_COLORS = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  researching: "bg-blue-100 text-blue-800 border-blue-200",
  testing: "bg-cyan-100 text-cyan-800 border-cyan-200",
  fact_checking: "bg-amber-100 text-amber-800 border-amber-200",
  legal_review: "bg-orange-100 text-orange-800 border-orange-200",
  ready_for_approval: "bg-indigo-100 text-indigo-800 border-indigo-200",
  published: "bg-emerald-100 text-emerald-800 border-emerald-200",
  update_required: "bg-yellow-100 text-yellow-800 border-yellow-200",
  archived: "bg-slate-200 text-slate-600 border-slate-300",
};

export default function EditorialStudio() {
  const [items, setItems] = useState([]);
  const [states, setStates] = useState({ states: [], transitions: {} });
  const [tools, setTools] = useState([]);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [newOpen, setNewOpen] = useState(false);

  const reload = () => bcReviews(filter).then((d) => setItems(d.items || []));
  useEffect(() => {
    document.title = "Editorial Studio | Business Center";
    bcReviewStates().then(setStates);
    bcTools().then((d) => setTools(d.items || []));
    reload();
  }, []); // eslint-disable-line
  useEffect(() => { reload(); }, [filter]); // eslint-disable-line

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Navbar />
      <main className="pt-24 pb-16 max-w-7xl mx-auto px-4 flex-1 w-full" data-testid="bc-editorial">
        <header className="mb-6 flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-indigo-700 font-bold flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Editorial Studio</div>
            <h1 className="text-3xl font-bold text-slate-900">Product review workflow</h1>
            <p className="text-sm text-slate-600 mt-1">A publishing pipeline that enforces sources, evidence and scoring before anything goes live.</p>
          </div>
          <button data-testid="bc-ed-new" onClick={() => setNewOpen(true)} className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold rounded-lg px-4 py-2 text-sm"><Plus size={14} /> New review</button>
        </header>

        <div className="flex flex-wrap gap-2 mb-4">
          <button data-testid="bc-ed-filter-all" onClick={() => setFilter("")} className={`text-xs px-3 py-1.5 rounded-full border ${!filter ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`}>All</button>
          {states.states.map((s) => (
            <button key={s} data-testid={`bc-ed-filter-${s}`} onClick={() => setFilter(s)} className={`text-xs px-3 py-1.5 rounded-full border ${filter === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`}>{s.replaceAll("_", " ")}</button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-1 bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100 max-h-[70vh] overflow-y-auto">
            {items.length === 0 && <div className="p-8 text-center text-sm text-slate-500">No reviews yet. Click "New review" to start.</div>}
            {items.map((r) => (
              <button key={r.id} data-testid={`bc-ed-item-${r.id.slice(0, 6)}`} onClick={() => bcReviewGet(r.id).then(setSelected)} className={`w-full text-left px-4 py-3 hover:bg-slate-50 ${selected?.id === r.id ? "bg-indigo-50" : ""}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold text-slate-900 text-sm truncate">{r.title}</div>
                  <span className={`text-[9px] uppercase tracking-widest font-bold border rounded-full px-1.5 py-0.5 ${STATE_COLORS[r.status] || STATE_COLORS.draft}`}>{r.status.replaceAll("_", " ")}</span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{r.tool_slug}</div>
              </button>
            ))}
          </div>

          <div className="lg:col-span-2">
            {!selected ? (
              <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-16 text-center text-slate-400 text-sm">Select a review on the left, or create a new one.</div>
            ) : (
              <ReviewEditor review={selected} states={states} onChange={(r) => { setSelected(r); reload(); }} onClose={() => setSelected(null)} />
            )}
          </div>
        </div>

        {newOpen && (
          <NewReviewModal tools={tools} onClose={() => setNewOpen(false)} onCreated={(r) => { setNewOpen(false); setSelected(r); reload(); }} />
        )}
      </main>
      <Footer />
    </div>
  );
}

function ReviewEditor({ review, states, onChange }) {
  const [r, setR] = useState(review);
  const [err, setErr] = useState(null);
  const [note, setNote] = useState("");

  useEffect(() => { setR(review); setErr(null); }, [review]);

  const patch = async (fields) => {
    const upd = await bcReviewPatch(r.id, fields);
    setR(upd); onChange(upd);
  };
  const move = async (to) => {
    setErr(null);
    try {
      const upd = await bcReviewTransition(r.id, to, note);
      setR(upd); onChange(upd); setNote("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Transition failed");
    }
  };
  const allowed = states.transitions?.[r.status] || [];

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="bc-ed-editor">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className={`text-[10px] uppercase tracking-widest font-bold border rounded-full px-2 py-0.5 ${STATE_COLORS[r.status] || STATE_COLORS.draft}`} data-testid="bc-ed-status">{r.status.replaceAll("_", " ")}</span>
          <span className="text-xs text-slate-500">{r.tool_slug}</span>
        </div>
        <div className="flex flex-wrap gap-1">
          {allowed.map((t) => (
            <button key={t} data-testid={`bc-ed-transition-${t}`} onClick={() => move(t)} className="text-[11px] px-2 py-1 rounded-md bg-slate-900 hover:bg-slate-700 text-white font-semibold inline-flex items-center gap-1">
              <ChevronRight size={10} /> {t.replaceAll("_", " ")}
            </button>
          ))}
        </div>
      </div>

      <input value={r.title || ""} onChange={(e) => setR({ ...r, title: e.target.value })} onBlur={() => patch({ title: r.title })} data-testid="bc-ed-title" className="mt-4 w-full text-2xl font-bold text-slate-900 border-b border-slate-200 py-1 focus:outline-none focus:border-indigo-500" />
      <textarea value={r.summary || ""} onChange={(e) => setR({ ...r, summary: e.target.value })} onBlur={() => patch({ summary: r.summary })} placeholder="Executive summary…" className="mt-3 w-full border border-slate-200 rounded-md px-3 py-2 text-sm min-h-[80px]" />

      {err && <div className="mt-3 text-xs text-red-800 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="bc-ed-err">{err}</div>}

      <div className="mt-4 grid md:grid-cols-2 gap-4">
        <ListEditor label="Scores (criterion: points)" items={Object.entries(r.scores || {}).map(([k, v]) => `${k}: ${v}`)}
          onChange={(lines) => {
            const s = {};
            lines.forEach((l) => { const [k, v] = l.split(":").map((x) => x.trim()); if (k && v) s[k] = Number(v) || 0; });
            patch({ scores: s });
          }} testid="bc-ed-scores" placeholder="business_value: 18" />
        <ListEditor label="Sources (one URL per line)" items={(r.sources || []).map((s) => s.url || s)} onChange={(lines) => patch({ sources: lines.map((u) => ({ url: u })) })} testid="bc-ed-sources" placeholder="https://…" />
        <ListEditor label="Evidence (one note per line)" items={(r.evidence || []).map((s) => s.note || s)} onChange={(lines) => patch({ evidence: lines.map((n) => ({ note: n })) })} testid="bc-ed-evidence" placeholder="Tested Aug 2026: …" />
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1 font-bold">Disclosures</div>
          <label className="flex items-center gap-2 text-xs text-slate-700"><input type="checkbox" checked={r.disclosures?.affiliate_relationship || false} onChange={(e) => patch({ disclosures: { ...r.disclosures, affiliate_relationship: e.target.checked } })} /> Affiliate relationship</label>
          <label className="flex items-center gap-2 text-xs text-slate-700 mt-1"><input type="checkbox" checked={r.disclosures?.sponsored || false} onChange={(e) => patch({ disclosures: { ...r.disclosures, sponsored: e.target.checked } })} /> Sponsored</label>
          <label className="flex items-center gap-2 text-xs text-slate-700 mt-1"><input type="checkbox" checked={r.disclosures?.ai_assistance_used || false} onChange={(e) => patch({ disclosures: { ...r.disclosures, ai_assistance_used: e.target.checked } })} /> AI drafting used</label>
        </div>
      </div>

      <textarea value={r.body || ""} onChange={(e) => setR({ ...r, body: e.target.value })} onBlur={() => patch({ body: r.body })} placeholder="Full review body…" className="mt-4 w-full border border-slate-200 rounded-md px-3 py-2 text-sm min-h-[240px]" data-testid="bc-ed-body" />

      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Transition note (optional)" className="mt-3 w-full border border-slate-200 rounded-md px-3 py-2 text-xs" />

      <div className="mt-6 text-[11px] text-slate-500 border-t border-slate-200 pt-3">
        <strong>Gates:</strong> publishing requires at least one score, one source, one evidence entry, and an Editor or Admin role.
      </div>
    </div>
  );
}

function ListEditor({ label, items, onChange, testid, placeholder }) {
  const [text, setText] = useState((items || []).join("\n"));
  useEffect(() => { setText((items || []).join("\n")); }, [items]);
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-slate-500 mb-1 font-bold">{label}</div>
      <textarea data-testid={testid} value={text} onChange={(e) => setText(e.target.value)} onBlur={() => onChange(text.split("\n").map((s) => s.trim()).filter(Boolean))} placeholder={placeholder} className="w-full border border-slate-200 rounded-md px-3 py-2 text-xs min-h-[80px] font-mono" />
    </div>
  );
}

function NewReviewModal({ tools, onClose, onCreated }) {
  const [form, setForm] = useState({ tool_slug: tools[0]?.slug || "", title: "", summary: "" });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900">New review</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <select data-testid="bc-ed-new-tool" value={form.tool_slug} onChange={(e) => setForm({ ...form, tool_slug: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
            {tools.map((t) => <option key={t.slug} value={t.slug}>{t.name}</option>)}
          </select>
          <input data-testid="bc-ed-new-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Review title" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
          <textarea value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} placeholder="Executive summary" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm min-h-[80px]" />
          <button
            data-testid="bc-ed-new-create"
            onClick={async () => { const r = await bcReviewCreate(form); onCreated(r); }}
            disabled={!form.tool_slug || !form.title}
            className="w-full bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white font-semibold rounded-lg px-4 py-2.5 inline-flex items-center justify-center gap-2"
          >
            Create <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
